import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, DatePicker, Form, InputNumber, Modal, Space, TimePicker, message } from "antd";
import dayjs from "dayjs";
import {
  addSprZalGrafUsl,
  createSprZalGrafSession,
  fetchSprZalGrafSession,
  fetchSprZalGrafUsl
} from "./spr-zal-api";

const WEEKDAY_OPTIONS = [
  { value: 1, label: "пн" },
  { value: 2, label: "вт" },
  { value: 3, label: "ср" },
  { value: 4, label: "чт" },
  { value: 5, label: "пт" },
  { value: 6, label: "сб" },
  { value: 0, label: "вс" }
];

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toTime = (value) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

const minutesOfDay = (value) => {
  if (!value?.isValid?.()) return null;
  return value.hour() * 60 + value.minute();
};

const timeToLocalIso = (date, time) => {
  if (!date?.isValid?.() || !time?.isValid?.()) return null;
  return date
    .hour(time.hour())
    .minute(time.minute())
    .second(0)
    .millisecond(0)
    .format("YYYY-MM-DDTHH:mm:ss");
};

/**
 * Копия сеанса на период (Delphi TFrmCopyGrafs).
 */
const SprZalGrafCopyModal = ({ open, idZal, idStr, zal, onClose, onDone }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState(null);

  const zalTimeBeg = useMemo(() => toTime(zal?.planTimeBeg) || dayjs().hour(9).minute(0), [zal?.planTimeBeg]);
  const zalTimeEnd = useMemo(() => toTime(zal?.planTimeEnd) || dayjs().hour(18).minute(0), [zal?.planTimeEnd]);

  useEffect(() => {
    if (!open || idZal == null || idStr == null) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([fetchSprZalGrafSession(idZal, idStr), fetchSprZalGrafUsl(idZal, idStr)])
      .then(([row, uslRows]) => {
        if (cancelled) return;
        const fdate = dayjs(pick(row, "fdate", "Fdate"));
        const timeBeg = toTime(pick(row, "timeBeg", "TimeBeg"));
        const timeEnd = toTime(pick(row, "timeEnd", "TimeEnd"));
        const usl = (Array.isArray(uslRows) ? uslRows : [])
          .map((u) => toNumber(pick(u, "idUsl", "IdUsl")))
          .filter((id) => id > 0);
        setSource({
          cntMax: toNumber(pick(row, "cntMax", "CntMax")) ?? 1,
          idSotr: toNumber(pick(row, "idSotr", "IdSotr")) || 0,
          idSotrRun: toNumber(pick(row, "idSotrRun", "IdSotrRun")) || 0,
          usl
        });
        form.setFieldsValue({
          period: [fdate.isValid() ? fdate : dayjs(), fdate.isValid() ? fdate : dayjs()],
          timeBeg,
          timeEnd,
          gap: 0,
          kol: 1,
          weekdays: [1, 2, 3, 4, 5]
        });
      })
      .catch((e) => {
        if (!cancelled) {
          message.error(e.message || "Не удалось загрузить сеанс");
          onClose?.();
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, idZal, idStr, form, onClose]);

  const refreshKol = () => {
    const timeBeg = form.getFieldValue("timeBeg");
    const timeEnd = form.getFieldValue("timeEnd");
    const gap = Number(form.getFieldValue("gap")) || 0;
    const seansLength = (minutesOfDay(timeEnd) ?? 0) - (minutesOfDay(timeBeg) ?? 0);
    if (seansLength <= 0) return;
    const startTime = minutesOfDay(timeBeg) ?? 0;
    const endTime = (minutesOfDay(zalTimeEnd) ?? 0) + gap;
    const qty = Math.floor((endTime - startTime) / (seansLength + gap));
    if (qty > 0) form.setFieldsValue({ kol: qty });
  };

  const onOk = async () => {
    if (!source) return;
    const values = await form.validateFields();
    const [beg, end] = values.period || [];
    if (!beg?.isValid?.() || !end?.isValid?.()) {
      message.error("Укажите период");
      return;
    }
    const seansLength = (minutesOfDay(values.timeEnd) ?? 0) - (minutesOfDay(values.timeBeg) ?? 0);
    if (seansLength <= 0) {
      message.error("Окончание должно быть позже начала");
      return;
    }
    const startTime = minutesOfDay(values.timeBeg) ?? 0;
    const gap = Number(values.gap) || 0;
    const kol = Math.max(0, Number(values.kol) || 0);
    const weekdays = new Set(values.weekdays || []);
    if (kol === 0 && beg.isSame(end, "day")) {
      message.warning("Нет сеансов для заполнения");
      return;
    }

    setSaving(true);
    let created = 0;
    try {
      let xDate = beg.startOf("day");
      const last = end.startOf("day");
      while (xDate.isBefore(last) || xDate.isSame(last, "day")) {
        const dow = xDate.day();
        for (let i = 1; i <= kol; i += 1) {
          const isSourceSlot = xDate.isSame(beg, "day") && i === 1;
          if (isSourceSlot) continue;
          if (!weekdays.has(dow)) continue;

          const begMin = startTime + (seansLength + gap) * (i - 1);
          const endMin = begMin + seansLength;
          const timeBeg = xDate.hour(Math.floor(begMin / 60)).minute(begMin % 60).second(0).millisecond(0);
          const timeEnd = xDate.hour(Math.floor(endMin / 60)).minute(endMin % 60).second(0).millisecond(0);

          const session = await createSprZalGrafSession(idZal, {
            fdate: xDate.format("YYYY-MM-DD"),
            timeBeg: timeToLocalIso(xDate, timeBeg),
            timeEnd: timeToLocalIso(xDate, timeEnd),
            cntMax: source.cntMax,
            idSotr: source.idSotr,
            idSotrRun: source.idSotrRun
          });
          const newId = toNumber(pick(session, "idStr", "IdStr"));
          if (newId != null) {
            for (const idUsl of source.usl) {
              try {
                await addSprZalGrafUsl(idZal, newId, idUsl);
              } catch {
                /* ignore */
              }
            }
            created += 1;
          }
        }
        xDate = xDate.add(1, "day");
      }
      message.success(created ? `Создано сеансов: ${created}` : "Новых сеансов не создано");
      onDone?.();
      onClose?.();
    } catch (e) {
      message.error(e.message || "Не удалось создать копии");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Копия сеанса"
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={480}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: 8 }}>
          <Button key="ok" type="primary" loading={saving} onClick={onOk}>
            OK
          </Button>
          <Button key="cancel" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" disabled={loading || saving}>
        <Form.Item label="Период" name="period" rules={[{ required: true, message: "Укажите период" }]}>
          <DatePicker.RangePicker format="DD.MM.YYYY" style={{ width: "100%" }} allowClear={false} />
        </Form.Item>
        <Space wrap size="middle">
          <Form.Item label="Начало" name="timeBeg" rules={[{ required: true }]}>
            <TimePicker format="HH:mm" allowClear={false} />
          </Form.Item>
          <Form.Item label="Окончание" name="timeEnd" rules={[{ required: true }]}>
            <TimePicker format="HH:mm" allowClear={false} />
          </Form.Item>
          <Form.Item label="Интервал, мин" name="gap">
            <InputNumber min={0} style={{ width: 100 }} />
          </Form.Item>
          <Form.Item label="Кол-во в день" name="kol">
            <InputNumber min={0} style={{ width: 100 }} />
          </Form.Item>
          <Button style={{ marginTop: 30 }} onClick={refreshKol}>
            Рассчитать
          </Button>
        </Space>
        <Form.Item label="Дни недели" name="weekdays">
          <Checkbox.Group options={WEEKDAY_OPTIONS} />
        </Form.Item>
        <div style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
          Интервал кабинета: {zalTimeBeg.format("HH:mm")} — {zalTimeEnd.format("HH:mm")}
        </div>
      </Form>
    </Modal>
  );
};

export default SprZalGrafCopyModal;
